// QuoteBuilder.jsx — BM-H77 Phase-2 quote form (BM-H123 disbursements added).
//
// Route: /quotes/new (create) and /quotes/:ref/edit (edit draft).
// Cards (per V's spec):
//   1. Client + Matter (Smart Contacts autocomplete, matter description)
//   2. Property + Transaction (type, tenure, address, price, buyer status
//      — non-UK resident hidden by default behind "More options")
//   3. Fee earner + Additional fees (default: Emily Howard)
//   3b. Disbursements (BM-H123 — grouped picker + auto HMLR registration fee)
//   4. Preview + Save/Send
//
// Save-and-send navigates to /quotes/:ref in send-review mode (never
// auto-sends — Phase-1 decision #4).

import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Save, Send, X, Plus, ChevronDown, ChevronUp, Search,
} from "lucide-react";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { STAFF } from "../config/staffConfig";
import { Button } from "../components/ui/button";
import { additionalFeeOption, bandLegalFee, catalogueAdditionalFee } from "../utils/quoteFees";

const DEFAULT_FE_INITIALS = "EH"; // V confirmed default = Emily Howard.
const EXPEDITED_COMPLETION_FEE = 500; // £500 + VAT.
const LEASEHOLD_ADD_ON_FEE = 350; // £350 auto add-on for leasehold (catalogue leasehold_supplement wins).
const LEASEHOLD_SUPPLEMENT_TXNS = ["purchase", "remortgage"];
// BM-H4496: company remortgage supplement, priced from the catalogue's
// acting_for_company_mortgage line.
const COMPANY_MORTGAGE_FEE_ID = "acting_for_company_mortgage";
// Rebekah, 19 Oct 2026: the shared ownership and transfer of equity
// supplements on a remortgage use the existing figures. Shared ownership is
// the £275 shared ownership purchase fee; a TOE takes the existing TOE
// purchase-side fee for the tenure (we act for the borrowers taking the
// property and the new loan).
const SO_MORTGAGE_FEE_ID = "shared_ownership_purchase";
const toeMortgageFee = (catalogue, tenure) =>
  catalogue?.transfer_fees?.[`toe_${tenure === "leasehold" ? "leasehold" : "freehold"}_purchase`] || null;

const TRANSACTION_OPTIONS = [
  { value: "purchase",           label: "Purchase" },
  { value: "sale",               label: "Sale" },
  { value: "transfer_of_equity", label: "Transfer of equity" },
  { value: "remortgage",         label: "Remortgage" },
];

const TENURE_OPTIONS = [
  { value: "freehold",  label: "Freehold" },
  { value: "leasehold", label: "Leasehold" },
];

function authHeaders() {
  const uid = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
  const email = sessionStorage.getItem("bonsai_user_email") || localStorage.getItem("bonsai_user_email") || uid;
  return { "X-Bonsai-User-Id": uid || email, "X-User-Email": email };
}

// Whole pounds round to whole pounds as before; amounts carrying pence
// (the £12.50 bank transfer fees) round to the penny, as the server does.
const roundLike = (x, basis) => (Number.isInteger(basis) ? Math.round(x) : Math.round(x * 100) / 100);

function fmtGbp(pounds) {
  const n = Number(pounds);
  if (!Number.isFinite(n)) return "£0";
  return new Intl.NumberFormat("en-GB", {
    style: "currency", currency: "GBP",
    // Pence only when there are pence (the £12.50 bank transfer fees).
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(n) ? 0 : 2,
  }).format(n);
}

// ─── Smart Contacts autocomplete ────────────────────────────────────
function SmartContactPicker({ value, onPick, onChangeText, testIdPrefix }) {
  const [query, setQuery] = useState(value?.name || "");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [zeroMatch, setZeroMatch] = useState(false);

  useEffect(() => { setQuery(value?.name || ""); }, [value?.name]);

  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults([]); setZeroMatch(false); return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(`${API}/hub/smart-contacts/search`, {
          headers: authHeaders(),
          params: { q: query.trim(), limit: 8 },
        });
        const items = r.data?.items || [];
        setResults(items);
        setZeroMatch(items.length === 0);
        setOpen(true);
      } catch {
        setResults([]); setZeroMatch(true); setOpen(true);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div className="relative" data-testid={`${testIdPrefix}-wrapper`}>
      <div className="relative">
        <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); onChangeText?.(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Start typing a client name..."
          data-testid={`${testIdPrefix}-input`}
          className="w-full bg-navy border border-white/10 rounded pl-9 pr-3 py-2 text-sm text-white"
        />
        {searching && <Loader2 className="w-4 h-4 animate-spin text-teal absolute right-3 top-1/2 -translate-y-1/2" />}
      </div>
      {open && (results.length > 0 || zeroMatch) && (
        <div data-testid={`${testIdPrefix}-dropdown`}
          className="absolute z-20 mt-1 w-full bg-navy-card border border-white/10 rounded shadow-lg max-h-60 overflow-auto">
          {results.map((r) => (
            <button key={r.id} type="button"
              onMouseDown={(e) => { e.preventDefault(); onPick(r); setOpen(false); }}
              data-testid={`${testIdPrefix}-option-${r.id}`}
              className="block w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/5">
              <div className="text-sm text-white">{r.name}</div>
              <div className="text-xs text-gray-400">
                {r.email || "no email"}{r.phone ? ` · ${r.phone}` : ""}
              </div>
            </button>
          ))}
          {zeroMatch && (
            <div data-testid={`${testIdPrefix}-zero-match`} className="px-3 py-2 text-xs text-gray-400">
              No existing contact — <span className="text-status-amber">(new contact)</span> will be created.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Additional fees multi-select ──────────────────────────────────
function AdditionalFeesPicker({ catalogue, selected, onChange, testIdPrefix, baseFee }) {
  const [query, setQuery] = useState("");
  // BM-H4496: catalogue items carry id / fee / fee_from, not code / amount.
  const additionalItems = useMemo(
    () => (catalogue?.additional_fees || []).map(f => additionalFeeOption(f, baseFee)),
    [catalogue, baseFee],
  );
  const filtered = useMemo(() => {
    if (!query.trim()) return additionalItems.slice(0, 12);
    const ql = query.toLowerCase();
    return additionalItems.filter(f =>
      (f.label || f.description || "").toLowerCase().includes(ql) ||
      (f.code || "").toLowerCase().includes(ql),
    ).slice(0, 12);
  }, [query, additionalItems]);

  const isPicked = (code) => selected.some(s => s.code === code);

  return (
    <div data-testid={`${testIdPrefix}-picker`}>
      <input
        type="text" value={query} onChange={(e) => setQuery(e.target.value)}
        placeholder="Search additional fees..."
        data-testid={`${testIdPrefix}-search`}
        className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white mb-2"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-48 overflow-auto">
        {filtered.map((f) => {
          const picked = isPicked(f.code);
          return (
            <button key={f.code} type="button"
              onClick={() => {
                if (picked) onChange(selected.filter(s => s.code !== f.code));
                else onChange([...selected, { ...f }]);
              }}
              data-testid={`${testIdPrefix}-toggle-${f.code}`}
              className={`text-left text-xs px-2 py-1.5 rounded border transition-colors ${
                picked ? "bg-teal/15 border-teal/40 text-teal"
                       : "bg-navy border-white/10 text-gray-300 hover:border-white/20"
              }`}>
              <span className="font-medium">{f.label || f.description}</span>
              <span className="text-gray-500"> — {f.is_from ? "from " : ""}{fmtGbp(f.amount)}</span>
            </button>
          );
        })}
      </div>
      {selected.length > 0 && (
        <div className="mt-3 space-y-1" data-testid={`${testIdPrefix}-selected`}>
          {selected.map((s, idx) => (
            <div key={`${s.code}-${idx}`}
              data-testid={`${testIdPrefix}-chip-${s.code}`}
              className="flex items-center gap-2 bg-navy border border-white/10 rounded px-2 py-1 text-xs">
              <span className="text-teal">{s.description}</span>
              <input
                type="number" value={s.amount}
                onChange={(e) => onChange(selected.map((x, i) =>
                  i === idx ? { ...x, amount: Number(e.target.value) || 0 } : x))}
                data-testid={`${testIdPrefix}-amount-${s.code}`}
                className="w-20 bg-transparent text-white text-xs px-1 border-b border-white/10" />
              <button type="button" onClick={() => onChange(selected.filter((_, i) => i !== idx))}
                data-testid={`${testIdPrefix}-remove-${s.code}`}
                className="text-gray-500 hover:text-status-red">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Disbursements picker (BM-H123) ────────────────────────────────────────────
//
// Groups the catalogue's disbursements[] by category, filters by
// transaction kind, and lets the fee earner tick items on / off. Range
// items (min–max) let them tweak the amount; "hmlr_scale" items are
// auto-priced from the property price via the client-side band tables.
function disbursementAppliesToTxn(item, txnKey, tenure) {
  const applies = item.applies_to || [];
  if (applies.length === 0) return true;
  if (applies.includes(txnKey)) return true;
  if (tenure === "leasehold" && applies.includes("leasehold")) return true;
  return false;
}

function computeHmlrFromBands(bands, price, lodgement) {
  if (!Array.isArray(bands) || !price) return null;
  const p = Number(price) || 0;
  for (const b of bands) {
    if (b.upper === null || b.upper === undefined || p <= b.upper) {
      return lodgement === "postal" ? Number(b.postal) : Number(b.portal);
    }
  }
  return null;
}

function disbursementLineFromItem(item, catalogue, price) {
  let amount = item.amount;
  if (item.kind === "hmlr_scale") {
    const scale = item.id === "hmlr_registration_scale_2" ? "scale_2" : "scale_1";
    const bands = catalogue?.[`hmlr_${scale}`] || [];
    amount = computeHmlrFromBands(bands, price, "portal");
  }
  return {
    code: item.id,
    // Keep both `label` (used by quote_pdf.py) and `description` (mirroring the
    // additional-fees / manual-line shape) so downstream renderers work either way.
    label: item.label,
    description: item.label,
    amount: amount == null ? 0 : Number(amount) || 0,
    vat: item.vat || "no_vat",
    kind: item.kind || "fixed",
    category: item.category || "Other",
    note: item.note || undefined,
  };
}

function DisbursementsPicker({ catalogue, selected, onChange, transactionType, tenure, price, testIdPrefix, catalogueKey = "disbursements", selectedLabel = "Selected disbursements" }) {
  const items = catalogue?.[catalogueKey] || [];
  const txnKey = (
    transactionType === "transfer_of_equity" ? "toe" : transactionType
  );

  const groups = useMemo(() => {
    const filtered = items.filter(it => disbursementAppliesToTxn(it, txnKey, tenure));
    const map = new Map();
    filtered.forEach(it => {
      const cat = it.category || "Other";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(it);
    });
    return Array.from(map.entries());
  }, [items, txnKey, tenure]);

  // Auto-price the HMLR scale line whenever price/txn changes
  useEffect(() => {
    if (!catalogue) return;
    let mutated = false;
    const next = selected.map(s => {
      if (s.kind === "hmlr_scale") {
        const scale = s.code === "hmlr_registration_scale_2" ? "scale_2" : "scale_1";
        const bands = catalogue?.[`hmlr_${scale}`] || [];
        const fresh = computeHmlrFromBands(bands, price, "portal");
        if (fresh != null && fresh !== s.amount) {
          mutated = true;
          return { ...s, amount: fresh };
        }
      }
      return s;
    });
    if (mutated) onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price, catalogue]);

  const isPicked = (id) => selected.some(s => s.code === id);

  return (
    <div data-testid={`${testIdPrefix}-picker`} className="space-y-3">
      {groups.map(([category, catItems]) => (
        <div key={category} className="border border-white/5 rounded p-2">
          <div className="text-xs font-bold text-teal mb-1.5">{category}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
            {catItems.map(it => {
              const picked = isPicked(it.id);
              const displayAmt = it.kind === "range"
                ? (it.amount_from != null && it.amount != null
                    ? `from \u00a3${it.amount_from}\u2013\u00a3${it.amount}`
                    : it.amount_from != null ? `from \u00a3${it.amount_from}` : fmtGbp(it.amount || 0))
                : it.kind === "hmlr_scale"
                  ? (() => {
                      const scale = it.id === "hmlr_registration_scale_2" ? "scale_2" : "scale_1";
                      const bands = catalogue?.[`hmlr_${scale}`] || [];
                      const f = computeHmlrFromBands(bands, price, "portal");
                      return f != null ? `\u00a3${f}` : "depends on price";
                    })()
                  : it.kind === "poa" || it.kind === "computed_or_poa"
                    ? "POA"
                    : fmtGbp(it.amount || 0);
              return (
                <button key={it.id} type="button"
                  onClick={() => {
                    if (picked) {
                      onChange(selected.filter(s => s.code !== it.id));
                    } else {
                      onChange([...selected, disbursementLineFromItem(it, catalogue, price)]);
                    }
                  }}
                  data-testid={`${testIdPrefix}-toggle-${it.id}`}
                  className={`text-left text-xs px-2 py-1.5 rounded border transition-colors ${
                    picked ? "bg-teal/15 border-teal/40 text-teal"
                           : "bg-navy border-white/10 text-gray-300 hover:border-white/20"
                  }`}>
                  <div className="font-medium">{it.label}</div>
                  <div className="text-gray-500">
                    {displayAmt}
                    {it.vat === "plus_vat" ? " + VAT"
                      : it.vat === "inc_vat" ? " inc VAT"
                      : it.vat === "ipt" ? " + IPT"
                      : " (VAT exempt)"}
                    {it.note ? ` \u2014 ${it.note}` : ""}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {selected.length > 0 && (
        <div className="space-y-1" data-testid={`${testIdPrefix}-selected`}>
          <div className="text-xs text-gray-400 mt-2">{selectedLabel}</div>
          {selected.map((s, idx) => (
            <div key={`${s.code}-${idx}`}
              data-testid={`${testIdPrefix}-chip-${s.code}`}
              className="flex items-center gap-2 bg-navy border border-white/10 rounded px-2 py-1 text-xs">
              <span className="text-teal flex-1">{s.description}</span>
              <span className="text-gray-500 text-[10px]">
                {s.vat === "plus_vat" ? "+VAT"
                  : s.vat === "inc_vat" ? "inc VAT"
                  : s.vat === "ipt" ? "+IPT"
                  : "exempt"}
              </span>
              <input type="number" value={s.amount}
                onChange={(e) => onChange(selected.map((x, i) =>
                  i === idx ? { ...x, amount: Number(e.target.value) || 0 } : x))}
                data-testid={`${testIdPrefix}-amount-${s.code}`}
                className="w-20 bg-transparent text-white text-xs px-1 border-b border-white/10 text-right" />
              <button type="button" onClick={() => onChange(selected.filter((_, i) => i !== idx))}
                data-testid={`${testIdPrefix}-remove-${s.code}`}
                className="text-gray-500 hover:text-status-red">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function QuoteBuilder({ editMode = false }) {
  const navigate = useNavigate();
  const { ref: routeRef } = useParams();

  // Card 1
  const [client, setClient] = useState({
    smart_contact_id: null, name: "", email: "", phone: "",
    first_name: "", last_name: "",
  });
  const [matterDescription, setMatterDescription] = useState("");

  // Card 2
  const [transactionType, setTransactionType] = useState("purchase");
  const [tenure, setTenure] = useState("freehold");
  const [propertyAddress, setPropertyAddress] = useState("");
  const [price, setPrice] = useState("");
  const [firstTimeBuyer, setFirstTimeBuyer] = useState(false);
  const [additionalProperty, setAdditionalProperty] = useState(false);
  const [buyingViaCompany, setBuyingViaCompany] = useState(false);
  const [companyMortgage, setCompanyMortgage] = useState(false);   // BM-H4496
  const [soMortgage, setSoMortgage] = useState(false);             // BM-H4496
  const [toeMortgage, setToeMortgage] = useState(false);           // BM-H4496
  const [nonUkResident, setNonUkResident] = useState(false);
  const [moreOptionsOpen, setMoreOptionsOpen] = useState(false);

  // Card 3
  const [feeEarnerInitials, setFeeEarnerInitials] = useState(DEFAULT_FE_INITIALS);
  const [expedited, setExpedited] = useState(false);
  const [isTest, setIsTest] = useState(false);   // BM-H85
  const [discountPct, setDiscountPct] = useState(0);   // BM-H128 — 0/5/10 only
  const [catalogue, setCatalogue] = useState(null);
  const [additionalFees, setAdditionalFees] = useState([]);
  const [customLines, setCustomLines] = useState([]);
  const [disbursements, setDisbursements] = useState([]); // BM-H123
  const [otherFees, setOtherFees] = useState([]); // BM-H3424

  // Card 4
  const [internalNotes, setInternalNotes] = useState("");
  const [clientNotes, setClientNotes] = useState("");

  // Live totals via server SDLT preview (fresh on every input change).
  const [sdlt, setSdlt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(editMode);

  const feDropdown = useMemo(
    () => STAFF.filter(s => s.is_active && (s.is_fee_earner || s.is_fe_dropdown))
              .sort((a, b) => a.full_name.localeCompare(b.full_name)),
    [],
  );

  // Load catalogue on mount.
  useEffect(() => {
    axios.get(`${API}/hub/quotes/catalogue`, { headers: authHeaders() })
      .then(r => setCatalogue(r.data))
      .catch(() => toast.error("Failed to load fee catalogue."));
  }, []);

  // If editing an existing draft, hydrate.
  useEffect(() => {
    if (!editMode || !routeRef) return;
    setLoading(true);
    axios.get(`${API}/hub/quotes/${routeRef}`, { headers: authHeaders() })
      .then(r => {
        const q = r.data || {};
        const c = q.client || {};
        setClient({
          smart_contact_id: c.smart_contact_id || null,
          name: c.name || [c.first_name, c.last_name].filter(Boolean).join(" "),
          email: c.email || "", phone: c.phone || "",
          first_name: c.first_name || "", last_name: c.last_name || "",
        });
        setTransactionType(q.transaction_type || "purchase");
        setTenure(q.property?.tenure || "freehold");
        setPropertyAddress(q.property?.address || "");
        setPrice(q.property?.price ? String(q.property.price) : "");
        setFeeEarnerInitials(q.fee_earner?.initials || DEFAULT_FE_INITIALS);
        setExpedited(Boolean(q.expedited_completion));
        setIsTest(Boolean(q.is_test));   // BM-H85
        // BM-H4496: the automatic lines (codes starting "_") are rebuilt from
        // the tenure / expedited / company boxes, so hydrating them as picked
        // fees as well counted them twice on every edit.
        const savedAdditional = (q.line_items?.additional) || [];
        setAdditionalFees(savedAdditional.filter(a => !String(a.code || "").startsWith("_")));
        setCompanyMortgage(savedAdditional.some(a => a.code === "_company_mortgage"));
        setSoMortgage(savedAdditional.some(a => a.code === "_so_mortgage"));
        setToeMortgage(savedAdditional.some(a => a.code === "_toe_mortgage"));
        setCustomLines((q.line_items?.manual) || []);
        setDisbursements((q.line_items?.disbursements) || []);   // BM-H123
        setOtherFees((q.line_items?.other_fees) || []);   // BM-H3424
        // BM-H128 — pick up existing discount when hydrating a draft/revised quote.
        const dp = Number(q.line_items?.discount_pct) || 0;
        setDiscountPct([0, 5, 10].includes(dp) ? dp : 0);
        setInternalNotes(q.notes_internal || "");
        setClientNotes(q.notes_client || "");
      })
      .catch(() => toast.error("Failed to load quote for editing."))
      .finally(() => setLoading(false));
  }, [editMode, routeRef]);

  // SDLT preview — refires when purchase/price/toggles change.
  useEffect(() => {
    if (transactionType !== "purchase" || !price) { setSdlt(0); return; }
    const timer = setTimeout(async () => {
      try {
        const r = await axios.post(
          `${API}/hub/quotes/${routeRef || "draft"}/sdlt-preview`,
          {
            purchase_price: Number(price) || 0,
            is_first_time_buyer: firstTimeBuyer,
            is_additional_property: additionalProperty,
            is_non_uk_resident: nonUkResident,
            is_company: buyingViaCompany,
          },
          { headers: authHeaders() },
        );
        setSdlt(Number(r.data?.total || r.data?.amount || 0));
      } catch { setSdlt(0); }
    }, 400);
    return () => clearTimeout(timer);
  }, [transactionType, price, firstTimeBuyer, additionalProperty, nonUkResident, buyingViaCompany, routeRef]);

  // BM-H4496: priced from the catalogue's price_bands / flat_fees and
  // remortgage_bands. Null means the catalogue has no fee for this
  // transaction, which the preview says rather than quietly showing £0.
  const bandFee = useMemo(
    () => bandLegalFee(catalogue, transactionType, tenure, price),
    [catalogue, transactionType, tenure, price],
  );
  const baseFee = bandFee ? bandFee.amount : 0;

  const additional = useMemo(() => {
    const arr = [...additionalFees];
    // Vanessa, 24 Oct 2026: the leasehold supplement is for leasehold
    // purchases and remortgages only, never sales (as the published fees say).
    if (tenure === "leasehold" && LEASEHOLD_SUPPLEMENT_TXNS.includes(transactionType)) {
      const lh = catalogueAdditionalFee(catalogue, "leasehold_supplement");
      const label = lh?.label || "Leasehold add-on";
      arr.push({ code: "_leasehold_addon", label, description: label, amount: lh?.fee ?? LEASEHOLD_ADD_ON_FEE });
    }
    if (transactionType === "remortgage" && companyMortgage) {
      const co = catalogueAdditionalFee(catalogue, COMPANY_MORTGAGE_FEE_ID);
      if (co) arr.push({ ...additionalFeeOption(co, baseFee), code: "_company_mortgage" });
    }
    if (transactionType === "remortgage" && soMortgage) {
      const so = catalogueAdditionalFee(catalogue, SO_MORTGAGE_FEE_ID);
      const label = "Shared ownership supplement";
      if (so) arr.push({ code: "_so_mortgage", label, description: label, amount: Number(so.fee), is_from: false });
    }
    if (transactionType === "remortgage" && toeMortgage) {
      const toe = toeMortgageFee(catalogue, tenure);
      const label = `Transfer of equity with remortgage (${tenure === "leasehold" ? "leasehold" : "freehold"})`;
      if (toe) arr.push({ code: "_toe_mortgage", label, description: label, amount: Number(toe.fee), is_from: false });
    }
    if (expedited) {
      arr.push({ code: "_expedited", label: "Expedited completion", description: "Expedited completion", amount: EXPEDITED_COMPLETION_FEE });
    }
    return arr;
  }, [additionalFees, tenure, expedited, catalogue, transactionType, companyMortgage, soMortgage, toeMortgage, baseFee]);

  const totals = useMemo(() => {
    const addTotal = additional.reduce((s, x) => s + (Number(x.amount) || 0), 0);
    const manualTotal = customLines.reduce((s, x) => s + (Number(x.amount) || 0), 0);
    const feesExBefore = baseFee + addTotal + manualTotal;
    // BM-H128 -- discount only applies to legal fees, never disbursements or SDLT.
    const discountAmount = roundLike((feesExBefore * (Number(discountPct) || 0)) / 100, feesExBefore);
    const feesEx = feesExBefore - discountAmount;
    const vat = roundLike(feesEx * 0.2, feesEx);
    // BM-H123: split disbursements by VAT treatment so the client sees
    // "+ VAT" lines gross up correctly. "plus_vat" items are grossed 1.2x;
    // inc_vat / no_vat / ipt items are passed through at face value.
    const disbGross = disbursements.reduce((s, d) => {
      const amt = Number(d.amount) || 0;
      if (d.vat === "plus_vat") return s + roundLike(amt * 1.2, amt);
      return s + amt;
    }, 0);
    // BM-H3424: Other Fees (CHAPS, Faster Payment, Search Pack, Digital
    // Onboarding, AML/ID checks) total separately from Disbursements --
    // the firm marks these up and can't always pin down the exact
    // third-party figure, so under the SRA Accounts Rules they aren't
    // disbursements. Same VAT-gross-up treatment, still counted in the
    // grand total, just its own line.
    const otherFeesGross = otherFees.reduce((s, f) => {
      const amt = Number(f.amount) || 0;
      if (f.vat === "plus_vat") return s + roundLike(amt * 1.2, amt);
      return s + amt;
    }, 0);
    return {
      fees_ex_vat_before_discount: feesExBefore,
      discount_pct: Number(discountPct) || 0,
      discount_amount: discountAmount,
      fees_ex_vat: feesEx,
      vat_on_fees: vat,
      disbursements: disbGross,
      other_fees: otherFeesGross,
      sdlt,
      grand_total: feesEx + vat + disbGross + otherFeesGross + sdlt,
    };
  }, [baseFee, additional, customLines, disbursements, otherFees, sdlt, discountPct]);

  async function save(mode) {
    setSaving(true);
    try {
      const nameParts = (client.name || "").trim().split(/\s+/);
      const firstName = client.first_name || nameParts[0] || "";
      const lastName  = client.last_name  || nameParts.slice(1).join(" ") || "";
      const payload = {
        client: {
          salutation: "", first_name: firstName, last_name: lastName,
          email: client.email, phone: client.phone,
          correspondence_address: "",
          name: client.name, smart_contact_id: client.smart_contact_id,
        },
        property: {
          address: propertyAddress,
          price: Number(price) || 0,
          tenure,
          transaction_kind: transactionType,
        },
        transaction_type: transactionType,
        line_items: {
          base_fee: {
            amount: baseFee, code: "base",
            label: bandFee?.label || "Legal fee", description: bandFee?.label || "Legal fee",
          },
          additional,
          // BM-H4496: the PDF prints `label`; custom lines only had `description`.
          manual: customLines.map(cl => ({ ...cl, label: cl.description })),
          disbursements,
          other_fees: otherFees,
          sdlt: { amount: sdlt },
          discount_pct: Number(discountPct) || 0,   // BM-H128
        },
        notes_internal: internalNotes,
        notes_client: clientNotes,
        expedited_completion: expedited,
        is_test: isTest,   // BM-H85
      };
      const headers = {
        ...authHeaders(),
        "X-Fee-Earner-Initials": feeEarnerInitials,
      };
      let saved;
      if (editMode && routeRef) {
        const r = await axios.patch(`${API}/hub/quotes/${routeRef}`, payload, { headers });
        saved = r.data;
      } else {
        const r = await axios.post(`${API}/hub/quotes`, payload, { headers });
        saved = r.data;
      }
      toast.success(mode === "send" ? "Draft saved. Opening send review..." : "Quote saved as draft.");
      if (mode === "send") {
        navigate(`/quotes/${saved.quote_ref}?send=1`);
      } else {
        navigate(`/quotes/${saved.quote_ref}`);
      }
    } catch (err) {
      const d = errorMessage(err, "Save failed.");
      toast.error(typeof d === "string" ? d : "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-navy p-6 text-white flex items-center justify-center"
        data-testid="quote-builder-loading">
        <Loader2 className="w-6 h-6 animate-spin text-teal" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy p-6 text-white" data-testid="quote-builder-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" onClick={() => navigate("/quotes")}
            data-testid="quote-builder-back"
            className="text-gray-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-1" /> Quotes
          </Button>
          <h1 className="text-xl font-bold">
            {editMode ? `Edit quote ${routeRef}` : "New quote"}
          </h1>
        </div>

        {/* ── Card 1: Client + Matter ─────────────────────────── */}
        <div className="bg-navy-card border border-white/10 rounded-lg p-4 mb-4" data-testid="quote-builder-card-client">
          <h2 className="text-sm font-bold text-teal mb-3">1. Client &amp; matter</h2>
          <label className="block text-xs text-gray-400 mb-1">Client name</label>
          <SmartContactPicker
            value={client}
            onChangeText={(txt) => setClient(c => ({ ...c, name: txt, smart_contact_id: null }))}
            onPick={(r) => setClient({
              smart_contact_id: r.id, name: r.name, email: r.email || "",
              phone: r.phone || "", first_name: "", last_name: "",
            })}
            testIdPrefix="quote-builder-client"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Client email <span className="text-status-red">*</span></label>
              <input type="email" value={client.email}
                onChange={(e) => setClient(c => ({ ...c, email: e.target.value }))}
                data-testid="quote-builder-client-email"
                className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Client phone</label>
              <input type="text" value={client.phone}
                onChange={(e) => setClient(c => ({ ...c, phone: e.target.value }))}
                data-testid="quote-builder-client-phone"
                className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white" />
            </div>
          </div>
          <label className="block text-xs text-gray-400 mb-1 mt-3">Matter description <span className="text-status-red">*</span></label>
          <input type="text" value={matterDescription}
            onChange={(e) => setMatterDescription(e.target.value)}
            placeholder="e.g. purchase of 10 High Street, Maidstone"
            data-testid="quote-builder-matter-description"
            className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white" />
        </div>

        {/* ── Card 2: Property + Transaction ──────────────────── */}
        <div className="bg-navy-card border border-white/10 rounded-lg p-4 mb-4" data-testid="quote-builder-card-property">
          <h2 className="text-sm font-bold text-teal mb-3">2. Property &amp; transaction</h2>
          <label className="block text-xs text-gray-400 mb-1">Transaction type</label>
          <div className="flex flex-wrap gap-2 mb-3">
            {TRANSACTION_OPTIONS.map(o => (
              <label key={o.value} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input type="radio" name="txn" value={o.value}
                  checked={transactionType === o.value}
                  onChange={() => setTransactionType(o.value)}
                  data-testid={`quote-builder-txn-${o.value}`}
                  className="accent-teal" />
                {o.label}
              </label>
            ))}
          </div>
          <label className="block text-xs text-gray-400 mb-1">Tenure</label>
          <div className="flex gap-4 mb-3">
            {TENURE_OPTIONS.map(o => (
              <label key={o.value} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input type="radio" name="tenure" value={o.value}
                  checked={tenure === o.value} onChange={() => setTenure(o.value)}
                  data-testid={`quote-builder-tenure-${o.value}`}
                  className="accent-teal" />
                {o.label}
              </label>
            ))}
          </div>
          <label className="block text-xs text-gray-400 mb-1">Property address</label>
          <textarea value={propertyAddress}
            onChange={(e) => setPropertyAddress(e.target.value)}
            data-testid="quote-builder-property-address" rows={2}
            className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white mb-3" />
          <label className="block text-xs text-gray-400 mb-1">
            {transactionType === "purchase" ? "Purchase price" : transactionType === "sale" ? "Sale price" : "Consideration"} (GBP)
          </label>
          <input type="number" value={price}
            onChange={(e) => setPrice(e.target.value)}
            data-testid="quote-builder-price"
            className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white mb-3" />

          {transactionType === "remortgage" && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input type="checkbox" checked={companyMortgage}
                  onChange={(e) => setCompanyMortgage(e.target.checked)}
                  data-testid="quote-builder-company-mortgage" className="accent-teal" />
                Company remortgage (+{fmtGbp(catalogueAdditionalFee(catalogue, COMPANY_MORTGAGE_FEE_ID)?.fee ?? 0)} plus VAT)
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input type="checkbox" checked={soMortgage}
                  onChange={(e) => setSoMortgage(e.target.checked)}
                  data-testid="quote-builder-so-mortgage" className="accent-teal" />
                Shared ownership (+{fmtGbp(catalogueAdditionalFee(catalogue, SO_MORTGAGE_FEE_ID)?.fee ?? 0)} plus VAT)
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input type="checkbox" checked={toeMortgage}
                  onChange={(e) => setToeMortgage(e.target.checked)}
                  data-testid="quote-builder-toe-mortgage" className="accent-teal" />
                Transfer of equity (+{fmtGbp(toeMortgageFee(catalogue, tenure)?.fee ?? 0)} plus VAT)
              </label>
            </div>
          )}
          {transactionType === "purchase" && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input type="checkbox" checked={firstTimeBuyer}
                  onChange={(e) => setFirstTimeBuyer(e.target.checked)}
                  data-testid="quote-builder-first-time-buyer" className="accent-teal" />
                First-time buyer
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input type="checkbox" checked={additionalProperty}
                  onChange={(e) => setAdditionalProperty(e.target.checked)}
                  data-testid="quote-builder-additional-property" className="accent-teal" />
                Additional property (buy-to-let / second home)
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input type="checkbox" checked={buyingViaCompany}
                  onChange={(e) => setBuyingViaCompany(e.target.checked)}
                  data-testid="quote-builder-buying-via-company" className="accent-teal" />
                Buying via company
              </label>
              <button type="button" onClick={() => setMoreOptionsOpen(o => !o)}
                data-testid="quote-builder-more-options-toggle"
                className="text-xs text-teal flex items-center gap-1 mt-2">
                {moreOptionsOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                More options
              </button>
              {moreOptionsOpen && (
                <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer ml-4">
                  <input type="checkbox" checked={nonUkResident}
                    onChange={(e) => setNonUkResident(e.target.checked)}
                    data-testid="quote-builder-non-uk-resident" className="accent-teal" />
                  Non-UK resident (+2% surcharge)
                </label>
              )}
            </div>
          )}
        </div>

        {/* ── Card 3: Fee earner + Additional fees ────────────── */}
        <div className="bg-navy-card border border-white/10 rounded-lg p-4 mb-4" data-testid="quote-builder-card-fees">
          <h2 className="text-sm font-bold text-teal mb-3">3. Fee earner &amp; additional fees</h2>
          <label className="block text-xs text-gray-400 mb-1">Fee earner</label>
          <select value={feeEarnerInitials}
            onChange={(e) => setFeeEarnerInitials(e.target.value)}
            data-testid="quote-builder-fee-earner"
            className="bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white mb-3">
            {feDropdown.map(fe => (
              <option key={fe.email} value={fe.initials}>{fe.full_name}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer mb-3">
            <input type="checkbox" checked={expedited}
              onChange={(e) => setExpedited(e.target.checked)}
              data-testid="quote-builder-expedited" className="accent-teal" />
            Expedited completion (+£500 plus VAT)
          </label>
          {/* BM-H128 — discretionary discount on legal fees only. Radio (not free text) so a mis-typed 15% can't ship. */}
          <div className="mb-3" data-testid="quote-builder-discount-group">
            <label className="block text-xs text-gray-400 mb-1">Discount on legal fees</label>
            <div className="flex gap-4">
              {[0, 5, 10].map(v => (
                <label key={v} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                  <input type="radio" name="discount-pct" value={v}
                    checked={Number(discountPct) === v}
                    onChange={() => setDiscountPct(v)}
                    data-testid={`quote-builder-discount-${v}`}
                    className="accent-teal" />
                  {v === 0 ? "No discount" : `${v}%`}
                </label>
              ))}
            </div>
          </div>
          {/* BM-H85 — training / demo quote. Excluded from funnel + nudge. */}
          <label className="flex items-center gap-2 text-sm text-amber-300 cursor-pointer mb-3">
            <input type="checkbox" checked={isTest}
              onChange={(e) => setIsTest(e.target.checked)}
              data-testid="quote-builder-is-test" className="accent-amber-500" />
            This is a test quote (excluded from reports and Mate nudges)
          </label>
          <label className="block text-xs text-gray-400 mb-1">Additional fees</label>
          <AdditionalFeesPicker
            catalogue={catalogue} selected={additionalFees} baseFee={baseFee}
            onChange={setAdditionalFees} testIdPrefix="quote-builder-additional"
          />
          <div className="mt-3">
            <Button type="button" size="sm" variant="ghost"
              onClick={() => setCustomLines([...customLines, { description: "", amount: 0 }])}
              data-testid="quote-builder-add-custom-line"
              className="text-xs text-teal">
              <Plus className="w-3 h-3 mr-1" /> Add custom line
            </Button>
            {customLines.map((cl, idx) => (
              <div key={idx} className="flex gap-2 mt-2"
                data-testid={`quote-builder-custom-line-${idx}`}>
                <input type="text" value={cl.description} placeholder="Description"
                  onChange={(e) => setCustomLines(customLines.map((x, i) =>
                    i === idx ? { ...x, description: e.target.value } : x))}
                  data-testid={`quote-builder-custom-line-desc-${idx}`}
                  className="flex-1 bg-navy border border-white/10 rounded px-2 py-1 text-xs text-white" />
                <input type="number" value={cl.amount} placeholder="0"
                  onChange={(e) => setCustomLines(customLines.map((x, i) =>
                    i === idx ? { ...x, amount: Number(e.target.value) || 0 } : x))}
                  data-testid={`quote-builder-custom-line-amount-${idx}`}
                  className="w-24 bg-navy border border-white/10 rounded px-2 py-1 text-xs text-white" />
                <button type="button" onClick={() => setCustomLines(customLines.filter((_, i) => i !== idx))}
                  data-testid={`quote-builder-custom-line-remove-${idx}`}
                  className="text-gray-500 hover:text-status-red">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* ── Card 3b: Disbursements (BM-H123) ───────────────── */}
        <div className="bg-navy-card border border-white/10 rounded-lg p-4 mb-4" data-testid="quote-builder-card-disbursements">
          <h2 className="text-sm font-bold text-teal mb-3">3b. Disbursements</h2>
          <p className="text-xs text-gray-400 mb-3">
            Third-party costs (searches, HMLR, notices, indemnities). Amounts default from the catalogue; edit inline if the third-party quote differs. HMLR registration fee is auto-priced from the purchase price.
          </p>
          <DisbursementsPicker
            catalogue={catalogue}
            selected={disbursements}
            onChange={setDisbursements}
            transactionType={transactionType}
            tenure={tenure}
            price={price}
            testIdPrefix="quote-builder-disbursements"
          />
        </div>

        {/* ── Card 3c: Other Fees (BM-H3424) ──────────────────── */}
        <div className="bg-navy-card border border-white/10 rounded-lg p-4 mb-4" data-testid="quote-builder-card-other-fees">
          <h2 className="text-sm font-bold text-teal mb-3">3c. Other Fees</h2>
          <p className="text-xs text-gray-400 mb-3">
            CHAPS, Faster Payment, Search Pack, Digital Onboarding and AML/ID checks (Thirdfort) are not disbursements under the SRA Accounts Rules — we apply a mark-up and the exact third-party cost isn't always determinable, so these are billed separately from both Legal Fees and Disbursements.
          </p>
          <DisbursementsPicker
            catalogue={catalogue}
            selected={otherFees}
            onChange={setOtherFees}
            transactionType={transactionType}
            tenure={tenure}
            price={price}
            testIdPrefix="quote-builder-other-fees"
            catalogueKey="other_fees"
            selectedLabel="Selected other fees"
          />
        </div>

        {/* ── Card 4: Preview + Save/Send ─────────────────────── */}
        <div className="bg-navy-card border border-white/10 rounded-lg p-4 mb-4" data-testid="quote-builder-card-preview">
          <h2 className="text-sm font-bold text-teal mb-3">4. Preview &amp; save</h2>
          {bandFee ? (
            <p className="text-xs text-gray-400 mb-2" data-testid="quote-builder-band-fee">
              {bandFee.label}: <span className="text-white">{fmtGbp(bandFee.amount)}</span>
            </p>
          ) : Number(price) > 0 && (
            <p className="text-xs text-status-amber mb-2" data-testid="quote-builder-no-band-fee">
              No catalogue legal fee for this transaction at this value. Add it as a custom line.
            </p>
          )}
          <div className="grid grid-cols-2 gap-y-1.5 text-sm mb-3" data-testid="quote-builder-totals">
            {/* BM-H128 — show the pre-discount sub-total + discount row only when a discount is applied. */}
            {totals.discount_pct > 0 && (
              <>
                <span className="text-gray-400">Fees ex VAT (before discount)</span>
                <span className="text-right text-gray-400" data-testid="quote-builder-total-fees-before-discount">{fmtGbp(totals.fees_ex_vat_before_discount)}</span>
                <span className="text-gray-400">Discount ({totals.discount_pct}%)</span>
                <span className="text-right text-white" data-testid="quote-builder-total-discount">-{fmtGbp(totals.discount_amount)}</span>
              </>
            )}
            <span className="text-gray-400">Fees ex VAT</span>
            <span className="text-right text-white" data-testid="quote-builder-total-fees">{fmtGbp(totals.fees_ex_vat)}</span>
            <span className="text-gray-400">Disbursements</span>
            <span className="text-right text-white" data-testid="quote-builder-total-disbursements">{fmtGbp(totals.disbursements)}</span>
            <span className="text-gray-400">Other Fees</span>
            <span className="text-right text-white" data-testid="quote-builder-total-other-fees">{fmtGbp(totals.other_fees)}</span>
            <span className="text-gray-400">SDLT</span>
            <span className="text-right text-white" data-testid="quote-builder-total-sdlt">{fmtGbp(totals.sdlt)}</span>
            <span className="text-gray-400">VAT</span>
            <span className="text-right text-white">{fmtGbp(totals.vat_on_fees)}</span>
            <span className="text-teal font-bold border-t border-white/10 pt-1.5">Total inc VAT</span>
            <span className="text-right text-teal font-bold border-t border-white/10 pt-1.5"
              data-testid="quote-builder-total-grand">{fmtGbp(totals.grand_total)}</span>
          </div>
          <label className="block text-xs text-gray-400 mb-1">Internal notes (not shown to client)</label>
          <textarea value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)}
            data-testid="quote-builder-internal-notes" rows={2}
            className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white mb-3" />
          <label className="block text-xs text-gray-400 mb-1">Client notes (appears on PDF)</label>
          <textarea value={clientNotes} onChange={(e) => setClientNotes(e.target.value)}
            data-testid="quote-builder-client-notes" rows={2}
            className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white mb-3" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => save("draft")} disabled={saving}
              data-testid="quote-builder-save-draft"
              className="text-gray-300 border-white/10">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save as draft
            </Button>
            <Button onClick={() => save("send")} disabled={saving}
              data-testid="quote-builder-save-send"
              className="bg-teal text-black hover:bg-teal-hover">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Save and send
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
